import React from "react";

import {
  SUGGESTED_CHAPTERS, buildChapters, chapterTitleOf, locateStep, usedChapterTitles,
} from "../territory_intro/chapters";

const DATALIST_ID = "step-editor-chapter-suggestions";

/**
 * CHAMP « CHAPITRE » d'une étape.
 *
 * CE QU'IL ÉDITE, ET CE QU'IL NE FAIT PAS. Il pose un nom sur l'étape, rien de
 * plus : le découpage en chapitres est déduit des étapes qui se suivent avec le
 * même nom (cf. territory_intro/chapters.js). Il n'y a donc pas de « créer un
 * chapitre », pas de « déplacer une étape dans un chapitre », pas de liste à
 * maintenir d'accord avec la séquence — écrire le même nom que l'étape d'avant
 * la range à sa suite, en écrire un autre ouvre un chapitre.
 *
 * C'est un mécanisme simple mais qui ne se devine pas : d'où la phrase qui
 * l'explique, et surtout la ligne de position — « 2ᵉ des 4 étapes du chapitre »
 * — qui montre le résultat du geste au lieu de le décrire.
 *
 * LES PASTILLES ne sont pas de la décoration. Un champ libre laissé seul
 * produit « Changement climatique » et « changement climatique » à deux étapes
 * d'intervalle, donc deux chapitres là où l'auteur en voulait un — et il ne le
 * verra qu'en séance. Reproposer d'un clic ce qui est déjà écrit dans le module
 * est la seule protection qui n'interdit rien.
 */
export default function ChapterField({ steps, stepIndex, onChange }) {
  const step = steps[stepIndex];
  const current = chapterTitleOf(step);

  const chapters = buildChapters(steps);
  const located = locateStep(chapters, stepIndex);

  const used = usedChapterTitles(steps);
  // Les suggestions déjà employées ne sont pas répétées : elles sont dans la
  // première rangée, qui a priorité — ce que l'auteur a écrit passe avant ce
  // que nous proposons.
  const suggestions = SUGGESTED_CHAPTERS.filter((title) => !used.includes(title));

  // Reprendre le chapitre de l'étape précédente est le geste le plus fréquent
  // (un chapitre, c'est plusieurs écrans de suite) et le plus fastidieux à
  // faire à la main. Proposé en tête, et seulement quand il change quelque
  // chose.
  const previousTitle = stepIndex > 0 ? chapterTitleOf(steps[stepIndex - 1]) : null;
  const canContinue = previousTitle && previousTitle !== current;

  const chip = (title, label, key) => (
    <button
      key={key || title || "none"}
      type="button"
      className={`step-chapter-chip${current === title ? " is-active" : ""}`}
      onClick={() => onChange(title || "")}
      aria-pressed={current === title}
    >
      {label || title}
    </button>
  );

  return (
    <>
      <h2 className="step-editor-section-title" style={{ marginTop: 22 }}>Chapitre</h2>
      <p className="step-editor-section-hint">
        Les étapes qui se suivent avec le même nom forment un chapitre. C'est ce nom que la
        salle voit pendant la séance, à la place du décompte d'étapes.
      </p>

      <input
        className="builder-input"
        list={DATALIST_ID}
        value={step.chapter || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Sans chapitre"
        aria-label="Nom du chapitre de cette étape"
      />
      <datalist id={DATALIST_ID}>
        {[...used, ...suggestions].map((title) => (
          <option key={title} value={title} />
        ))}
      </datalist>

      {canContinue && (
        <div className="step-chapter-chips">
          {chip(previousTitle, `↳ Suite de « ${previousTitle} »`, "continue")}
        </div>
      )}

      {used.length > 0 && (
        <>
          <span className="step-chapter-chips-label">Déjà dans ce module</span>
          <div className="step-chapter-chips">
            {used.map((title) => chip(title))}
            {current && chip(null, "Sans chapitre", "none")}
          </div>
        </>
      )}

      {suggestions.length > 0 && (
        <>
          <span className="step-chapter-chips-label">Suggestions</span>
          <div className="step-chapter-chips">{suggestions.map((title) => chip(title))}</div>
        </>
      )}

      {/* LE RÉSULTAT DU GESTE, pas sa description. C'est cette ligne qui rend
          visible la règle du « même nom à la suite » : on y voit l'étape
          rejoindre le groupe précédent, ou en ouvrir un nouveau. */}
      {located && (
        <div className="step-editor-status is-ok" style={{ marginTop: 12 }}>
          {located.chapter.title ? (
            <>
              Étape {located.stepInChapter} sur {located.chapter.count} du chapitre
              {" "}« {located.chapter.title} » · chapitre {located.chapterIndex + 1} sur{" "}
              {chapters.length}
            </>
          ) : (
            <>
              Cette étape n'appartient à aucun chapitre
              {chapters.length > 1 ? " : elle coupe le découpage en deux." : "."}
            </>
          )}
        </div>
      )}
    </>
  );
}
